/* eslint-disable */
import React, {
  useState,
  useEffect,
  useMemo,
  useRef,
  lazy,
  Suspense,
} from 'react';

// Draggable Scroll
import { useDraggable } from 'react-use-draggable-scroll';

// Redux
import { useSelector } from 'react-redux';

// Material
import Box from '@material-ui/core/Box';
import Table from '@material-ui/core/Table';
import TableContainer from '@material-ui/core/TableContainer';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableHead from '@material-ui/core/TableHead';
import Grid from '@material-ui/core/Grid';
import TableRow from '@material-ui/core/TableRow';
import Typography from '@material-ui/core/Typography';
import Divider from '@material-ui/core/Divider';
import IconButton from '@material-ui/core/IconButton';
import Switch from '@material-ui/core/Switch';

// Material Icons
import KeyboardArrowDownIcon from '@material-ui/icons/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@material-ui/icons/KeyboardArrowUp';

// Immer
import produce from 'immer';

// Types
import { ApplicationState } from 'store';

// Helpers
import { currencyFormat } from 'helpers/currencyFormat';
import useWindowDimensions from 'helpers/useWindowDimensions';
import {
  headerCsvFormat,
  headerXlsFormat,
  rowsXlsFormat,
} from 'helpers/reports';

// Contants
import IMAGES from 'constants/images';

// Components
import { XlsHeaderInterface, XlsRowInterface } from 'components';

// Lazy Components
const DialogueExport = lazy(() =>
  import('components').then((mod) => ({ default: mod.DialogueExport })),
);

// Styles
import useStyles from './tableCarDemand.styles';

interface TableCarDemandInterface {
  csvRef: any;
  periodString?: string;
  setTableCSV: (arg: any) => void;
}

type Props = TableCarDemandInterface;

const DRAWER_WIDTH = 300;
const BORDER_AND_SPACINGS = 80;

const KPIS_DISPLAY = {
  ipk: 'IPK',
  ipke: 'IPKE',
  valuePerKm: 'R$/Km',
  km: 'KM',
  idleKm: 'KM Ocioso',
  trips: 'Viagens',
};

const TableCarDemand: React.FC<Props> = ({
  csvRef,
  periodString,
  setTableCSV,
}) => {
  const classes = useStyles();
  const parentRef: any = useRef();
  const { events } = useDraggable(parentRef);

  const { width: windowWidth } = useWindowDimensions();

  const [openCollapseMetrics, setOpenCollapseMetrics] = useState(true);
  const [openCollapseShipments, setOpenCollapseShipments] = useState(true);
  const [stateWith, setStateWith] = useState(true);
  const [simplePdfData, setSimplePdfData] = useState<{
    title: string;
    subtitle: string;
    filters: string;
    headers: any;
    rows: any;
  }>({
    title: '',
    subtitle: '',
    filters: '',
    headers: [],
    rows: [],
  });

  const drawerOpen = useSelector(
    (state: ApplicationState) => state.drawer.open,
  );
  const dialog = useSelector((state: ApplicationState) => state.dialogs.dialog);

  const [benefitsList, setBenefitsList] = useState<string[]>([]);
  const [tableReference, setTableReference] = useState<any>();
  const [xlsHeader, setXlsHeader] = useState<XlsHeaderInterface[]>();
  const [xlsRows, setXlsRows] = useState<Array<XlsRowInterface[]>>([]);

  const carReports = useSelector(
    (state: ApplicationState) => state.fleet.reports || null,
  );

  useEffect(() => {
    setBenefitsList(
      produce(benefitsList, () => {
        const benefitsList: string[] = [];
        if (carReports) {
          for (const car in carReports) {
            const { benefits = {} } = carReports[car];
            for (const benefit of Object.keys(benefits)) {
              if (!benefitsList.includes(benefit)) {
                benefitsList.push(benefit);
              }
            }
          }
        }
        return benefitsList.sort();
      }),
    );
  }, [carReports]);

  const totalTransacted = useMemo(
    () =>
      carReports &&
      Object.entries(carReports).reduce(
        (total, [_, value]) => total + value.totalValue!,
        0,
      ),
    [carReports],
  );

  const width = useMemo(() => {
    if (drawerOpen) {
      return windowWidth - (DRAWER_WIDTH + BORDER_AND_SPACINGS);
    }
    return windowWidth - BORDER_AND_SPACINGS * 2;
  }, [windowWidth, drawerOpen]);

  const renderBenefit = useMemo(
    () => (benefits: object, benefit: string) => {
      return benefits.hasOwnProperty(benefit)
        ? // @ts-ignore
          Number(benefits[benefit])
          ? // @ts-ignore
            benefits[benefit]
          : 0
        : // @ts-ignore
          (benefits[benefit] = 0);
    },
    [],
  );

  const getBenefitCount = useMemo(
    () => (benefit: string) => {
      let total = 0;
      for (const entry of Object.values(carReports)) {
        // @ts-ignore
        if (
          entry.benefitsCount.hasOwnProperty(benefit) &&
          entry.benefitsCount[benefit as keyof typeof entry.benefitsCount]
        ) {
          // @ts-ignore
          total += entry.benefitsCount[benefit];
        }
      }
      return total;
    },
    [carReports],
  );

  const getLineBenefitCount = useMemo(
    () => (benefitsCount: object, benefit: string) => {
      // @ts-ignore
      if (benefitsCount.hasOwnProperty(benefit)) return benefitsCount[benefit];
      else return 0;
    },
    [carReports],
  );

  const getValue = useMemo(
    () => (benefit: string) => {
      let total = 0;
      for (const entry of Object.values(carReports)) {
        // @ts-ignore
        if (entry.benefits.hasOwnProperty(benefit) && entry.benefits[benefit]) {
          // @ts-ignore
          total += entry.benefits[benefit];
        }
      }
      return total;
    },
    [carReports],
  );

  const getTotalValue = useMemo(
    () => (benefits: object) => {
      return Object.entries(benefits).reduce(
        // @ts-ignore
        (total, [_, value]) => (total += Number(value) ? value : 0),
        0,
      );
    },
    [],
  );

  const getBenefitCountValue = useMemo(
    () => (benefitsCount: object) => {
      return Object.entries(benefitsCount).reduce(
        // @ts-ignore
        (total, [_, value]) => (total += Number(value) ? value : 0),
        0,
      );
    },
    [],
  );

  const totalValue = useMemo(() => {
    return Object.values(carReports)
      .map(({ benefits }) =>
        Object.entries(benefits).reduce(
          // @ts-ignore
          (total, [_, value]) => (total += Number(value) ? value : 0),
          0,
        ),
      )
      .reduce((acc, curr) => acc + curr, 0);
  }, [carReports]);

  const getBenefitCountTotalValue = useMemo(() => {
    let total = 0;
    for (const entry of Object.values(carReports)) {
      for (const benefit of Object.keys(entry.benefitsCount)) {
        // @ts-ignore
        if (entry.benefitsCount[benefit]) {
          // @ts-ignore
          total += entry.benefitsCount[benefit];
        }
      }
    }
    return total;
  }, [carReports]);

  const getKpisTotals = useMemo(() => {
    const idleKm = Object.values(carReports).reduce(
      (acc, curr) => acc + curr.kpis.idleKm,
      0,
    );
    const ipk =
      Object.values(carReports).reduce((acc, curr) => acc + curr.kpis.ipk, 0) /
        Object.values(carReports).length || 0;
    const ipke =
      Object.values(carReports).reduce((acc, curr) => acc + curr.kpis.ipke, 0) /
        Object.values(carReports).length || 0;
    const km = Object.values(carReports).reduce(
      (acc, curr) => acc + curr.kpis.km,
      0,
    );
    const kvd = Object.values(carReports).reduce(
      (acc, curr) => acc + curr.kpis.kvd,
      0,
    );
    const pvd = Object.values(carReports).reduce(
      (acc, curr) => acc + curr.kpis.pvd,
      0,
    );
    const trips = Object.values(carReports).reduce(
      (acc, curr) => acc + curr.kpis.trips,
      0,
    );
    const valuePerKm =
      Object.values(carReports).reduce(
        (acc, curr) => acc + curr.kpis.valuePerKm,
        0,
      ) / Object.values(carReports).length || 0;

    return {
      idleKm,
      ipk,
      ipke,
      km,
      kvd,
      pvd,
      trips,
      valuePerKm,
    };
  }, [carReports]);

  // generating the table csv
  useEffect(() => {
    let csv = '';
    let header = 'Embarques;';

    if (benefitsList) {
      Object.keys(carReports)
        .sort()
        .forEach((key, i, arr) => {
          header += `${headerCsvFormat(key)};`;
        });
      header += `${headerCsvFormat('Total')}\n`;
      csv += header;

      Object.keys(KPIS_DISPLAY)
        .sort()
        .forEach((key, i, arr) => {
          csv += `${
            KPIS_DISPLAY[key as keyof typeof KPIS_DISPLAY]
          };${Object.values(carReports)
            .map((e) => e.kpis[key as keyof typeof KPIS_DISPLAY] + ';')
            .join(';')};${getKpisTotals[
            key as keyof typeof getKpisTotals
          ].toFixed(2)};\n`;
        });

      csv += benefitsList
        .map((benefit) => {
          let contentAux1 = '';

          Object.entries(carReports)
            .sort()
            .forEach(([key, { benefits, benefitsCount }]) => {
              contentAux1 =
                contentAux1 +
                `${getLineBenefitCount(benefitsCount, benefit) ||
                  0};${renderBenefit(benefits, benefit)};`;
            });

          return `${benefit};${contentAux1}${getBenefitCount(benefit) ||
            0};${getValue(benefit)}\n`;
        })
        .join('');

      let contentTotal = 'Total;';
      Object.entries(carReports)
        .sort()
        .map(([key, { benefits, benefitsCount }]) => {
          contentTotal += `${getBenefitCountValue(
            benefitsCount,
          )};${getTotalValue(benefits)};`;
        });

      csv += contentTotal + `${getBenefitCountTotalValue};${totalValue}\n`;
      setTableCSV(csv);
    }

    setTableCSV(csv);
  }, [benefitsList, setTableCSV]);

  useEffect(() => {
    const firstColumn = ['Embarques'];
    const dynamicHeader = Object.keys(carReports)
      ?.sort()
      ?.map((key) => key);
    const lastColumn = ['Total'];

    const headerSimplePdf = firstColumn
      .concat(dynamicHeader)
      .concat(lastColumn);

    const metricLabels = Object.keys(KPIS_DISPLAY)
      .sort()
      .map((kpi: string) => KPIS_DISPLAY[kpi as keyof typeof KPIS_DISPLAY]);

    const metricRows = stateWith
      ? Object.keys(KPIS_DISPLAY)
          .sort()
          .map((kpi: string) =>
            Object.entries(carReports)
              .sort()
              .flatMap(
                ([key, { kpis }]) => kpis[kpi as keyof typeof kpis] || 0,
              ),
          )
      : [];

    const combinedArray = stateWith
      ? metricLabels.map((label, index) => {
          const rowSum = metricRows[index].reduce(
            (sum, value) => sum + value,
            0,
          );

          const formattedRow = metricRows[index].map((value) =>
            value.toFixed(2),
          );

          return [label, ...formattedRow, rowSum.toFixed(2)];
        })
      : [];

    const profileLabels =
      benefitsList?.length > 0
        ? benefitsList.map((benefit: string) => benefit)
        : [];

    const profileRows =
      benefitsList?.length > 0
        ? benefitsList.map((benefit: string) =>
            Object.entries(carReports)
              .sort()
              .map(([key, { benefits, benefitsCount }]) => {
                const benefitValue =
                  getLineBenefitCount(benefitsCount, benefit) || 0;
                const totalValue = currencyFormat({
                  value: renderBenefit(benefits, benefit),
                });

                return `${benefitValue} (${totalValue})`;
              })
              .concat([
                `${getBenefitCount(benefit)} (${currencyFormat({
                  value: getValue(benefit),
                })})`,
              ]),
          )
        : [];

    const lastRow = Object.entries(carReports)
      .sort()
      .map(
        ([key, { benefits, benefitsCount }], i) =>
          `${getBenefitCountValue(benefitsCount)} (${currencyFormat({
            value: getTotalValue(benefits),
          })})`,
      )
      .concat([
        `${getBenefitCountTotalValue} (${currencyFormat({
          value: totalValue,
        })})`,
      ]);

    const combinedProfilesArray = profileLabels.map((label, index) => {
      return [label, ...profileRows[index]];
    });

    const combinedProfilesArrayWithTotal = [
      ...combinedProfilesArray,
      ['Total'].concat(lastRow),
    ];

    const tempSimplePdfData = {
      title: 'ONBOARD',
      subtitle: 'Relatório de Demanda por Carro',
      filters: periodString || '',
      headers: headerSimplePdf,
      rows: stateWith
        ? combinedArray.concat(combinedProfilesArrayWithTotal)
        : combinedProfilesArrayWithTotal,
    };

    setSimplePdfData(tempSimplePdfData);
  }, [carReports, benefitsList, stateWith, periodString]);
  useEffect(() => {
    const dynamicXlsHeader = Object.keys(carReports)
      ?.sort()
      ?.map((key) => ({
        header: key,
        key: key,
        width: 20,
      }));

    setXlsHeader(
      headerXlsFormat([
        {
          header: 'Embarques',
          key: 'labels',
          width: 20,
        },
        ...dynamicXlsHeader,
        {
          header: 'Total',
          key: 'totalValue',
          width: 20,
        },
      ]),
    );

    const firstMestricsRows = [
      { key: 'labels', value: 'Métricas', image: null },
      ...Object.keys(carReports)
        .sort()
        .map((kpi: string, i) => ({ key: kpi, value: '', image: null })),
      { key: 'totalValue', value: '', image: null },
    ];
    const metricRows = stateWith
      ? Object.keys(KPIS_DISPLAY)
          .sort()
          .map((kpi: string) => [
            {
              key: 'labels',
              value: `${KPIS_DISPLAY[kpi as keyof typeof KPIS_DISPLAY]}`,
              image: null,
            },
            ...Object.entries(carReports)
              .sort()
              .flatMap(([key, { kpis }], i) => ({
                key: Object.keys(carReports).sort()[i],
                value: `${kpis[kpi as keyof typeof kpis] || '0'}`,
                image: null,
              })),
            {
              key: 'totalValue',
              value: `${getKpisTotals[
                kpi as keyof typeof getKpisTotals
              ].toFixed(2)}`,
              image: null,
            },
          ])
      : [];

    const combinedMetricsArray = [firstMestricsRows, ...metricRows];

    const firstProfileRow = [
      { key: 'labels', value: 'Embarques', image: null },
      ...Object.keys(carReports)
        .sort()
        .map((kpi: string, i) => ({ key: kpi, value: '', image: null })),
      { key: 'totalValue', value: '', image: null },
    ];

    const profileRows = benefitsList.map((benefit) => [
      {
        key: 'labels',
        value: `${benefit}`,
        image: null,
      },
      ...Object.entries(carReports)
        .sort()
        .map(([key, { benefits, benefitsCount }], i) => ({
          key: Object.keys(carReports).sort()[i],
          value: `${getLineBenefitCount(benefitsCount, benefit) ||
            0}(${currencyFormat({
            value: renderBenefit(benefits, benefit),
          })})`,
          image: null,
        })),
      {
        key: 'totalValue',
        value: `${getBenefitCount(benefit) || 0} (${currencyFormat({
          value: getValue(benefit),
        })})`,
        image: null,
      },
    ]);

    const combinedProfilesArray = [firstProfileRow, ...profileRows];
    const lastRow = [
      {
        key: 'labels',
        value: `Total`,
        image: null,
      },
      ...Object.entries(carReports)
        .sort()
        .map(([key, { benefits, benefitsCount }], i) => ({
          key: Object.keys(carReports).sort()[i],
          value: `${getBenefitCountValue(benefitsCount)} (${currencyFormat({
            value: getTotalValue(benefits),
          })})`,
          image: null,
        })),
      {
        key: 'totalValue',
        value: `${getBenefitCountTotalValue} (${currencyFormat({
          value: totalValue,
        })})`,
        image: null,
      },
    ];
    setXlsRows(
      rowsXlsFormat([
        ...combinedMetricsArray,
        ...combinedProfilesArray,
        lastRow,
      ]) as XlsRowInterface[][],
    );
  }, [carReports, benefitsList, stateWith, periodString]);

  return (
    <Grid ref={(node: unknown) => setTableReference(node)}>
      <Grid
        container
        justifyContent='space-between'
        className={classes.gridContainer}>
        <Grid item xs>
          <Grid
            container
            spacing={2}
            justifyContent='flex-end'
            className={classes.resumeContainer}>
            <Grid container justifyContent='flex-end' alignItems='center'>
              <Box display='flex' alignItems='center'>
                <Box className={classes.resume}>
                  <Box className={classes.contentImage}>
                    <img
                      src={IMAGES.REPORTS_ICONS.TOTAL_ENTRY}
                      alt='total entry'
                    />
                  </Box>
                  <Divider
                    className={classes.dividerVertical}
                    orientation='vertical'
                    flexItem
                  />{' '}
                  <Grid className={classes.contentTotalEntry}>
                    <Typography className={classes.subTiltle}>
                      Transacionado
                    </Typography>
                    <Typography className={classes.value}>
                      {currencyFormat({ value: totalTransacted })}
                    </Typography>
                  </Grid>
                </Box>
                <Suspense fallback={'Carregando...'}>
                  <DialogueExport
                    isDialogPrint={true}
                    tableInstance={tableReference}
                    csvRef={csvRef}
                    simplePdf
                    simplePdfData={simplePdfData}
                    columnsPerPage={5}
                    pdfName='demanda_por_carro'
                    xlsTitle='Relatório de Demanda por carro'
                    xlsHeader={xlsHeader}
                    xlsRows={xlsRows}
                    pdf
                    csv
                    xls
                  />
                </Suspense>
              </Box>
            </Grid>
            <Box display='flex' alignItems='center' mt={1}>
              <Switch
                checked={stateWith}
                onChange={(e) => setStateWith((s) => !s)}
                inputProps={{ 'aria-label': 'Switch demo' }}
                className={classes.switch}
              />
              <Typography>Métricas operacionais</Typography>
            </Box>
          </Grid>
        </Grid>
      </Grid>
      {dialog.open && dialog.isDialogPrint && (
        <Typography color='inherit' variant='h4'>
          Relatórios de Operação • Demanda por Carro
        </Typography>
      )}
      <Box m={2}>
        <TableContainer
          className={classes.tableContainer}
          style={{ overflow: 'auto', width }}
          {...events}
          ref={parentRef}>
          <Table
            className={classes.table}
            aria-label='demanda por carro'
            style={{ overflow: 'auto', width }}>
            <TableHead>
              <TableRow>
                <TableCell align='left' variant='head'>
                  Embarques
                </TableCell>{' '}
                {carReports &&
                  Object.keys(carReports)
                    .sort()
                    .map((key) => (
                      <TableCell key={key} align='left' variant='head'>
                        {key}
                      </TableCell>
                    ))}
                <TableCell align='left' variant='head'>
                  Total
                </TableCell>
                <TableCell align='left' variant='head' />
              </TableRow>
            </TableHead>
            <TableBody>
              {stateWith && (
                <TableRow className={classes.trWithBorder}>
                  <TableCell>
                    <b>Métricas</b>
                  </TableCell>
                  {Object.entries(carReports)
                    .sort()
                    .map(() => (
                      <TableCell></TableCell>
                    ))}
                  <TableCell></TableCell>
                  <TableCell>
                    {' '}
                    <Box
                      display='flex'
                      alignItems='center'
                      justifyContent='space-between'>
                      <Box display='flex' alignItems='center'>
                        <IconButton
                          aria-label='expand row'
                          size='small'
                          onClick={() => setOpenCollapseMetrics((o) => !o)}>
                          {openCollapseMetrics ? (
                            <KeyboardArrowUpIcon />
                          ) : (
                            <KeyboardArrowDownIcon />
                          )}
                        </IconButton>
                      </Box>
                    </Box>
                  </TableCell>
                </TableRow>
              )}

              {stateWith &&
                openCollapseMetrics &&
                Object.keys(KPIS_DISPLAY)
                  .sort()
                  .map((kpi: string) => (
                    <TableRow key={kpi}>
                      <TableCell>
                        {KPIS_DISPLAY[kpi as keyof typeof KPIS_DISPLAY]}
                      </TableCell>
                      {Object.entries(carReports)
                        .sort()
                        .map(([key, { kpis }]) => (
                          <TableCell key={key}>
                            {kpis[kpi as keyof typeof kpis] || '0'}
                          </TableCell>
                        ))}
                      <TableCell>
                        {getKpisTotals[
                          kpi as keyof typeof getKpisTotals
                        ].toFixed(2)}
                      </TableCell>
                      <TableCell></TableCell>
                    </TableRow>
                  ))}

              <TableRow className={classes.trWithBorder}>
                <TableCell>
                  <b>Embarques</b>
                </TableCell>
                {Object.entries(carReports)
                  .sort()
                  .map(() => (
                    <TableCell></TableCell>
                  ))}
                <TableCell></TableCell>

                <TableCell>
                  <Box
                    display='flex'
                    alignItems='center'
                    justifyContent='space-between'>
                    <Box display='flex' alignItems='center'>
                      <IconButton
                        aria-label='expand row'
                        size='small'
                        onClick={() => setOpenCollapseShipments((o) => !o)}>
                        {openCollapseShipments ? (
                          <KeyboardArrowUpIcon />
                        ) : (
                          <KeyboardArrowDownIcon />
                        )}
                      </IconButton>
                    </Box>
                  </Box>
                </TableCell>
              </TableRow>

              {openCollapseShipments &&
                Array.isArray(benefitsList) &&
                benefitsList.map((benefit) => (
                  <TableRow key={benefit}>
                    <TableCell>{benefit}</TableCell>
                    {Object.entries(carReports)
                      .sort()
                      .map(([key, { benefits, benefitsCount }]) => (
                        <TableCell key={key}>
                          {getLineBenefitCount(benefitsCount, benefit) || 0} (
                          {currencyFormat({
                            value: renderBenefit(benefits, benefit),
                          })}
                          )
                        </TableCell>
                      ))}
                    <TableCell>
                      {getBenefitCount(benefit) || 0} (
                      {currencyFormat({ value: getValue(benefit) })})
                    </TableCell>
                    <TableCell></TableCell>
                  </TableRow>
                ))}

              <TableRow>
                <TableCell>
                  <b>Total</b>
                </TableCell>
                {Object.entries(carReports)
                  .sort()
                  .map(([key, { benefits, benefitsCount }]) => (
                    <TableCell key={key}>
                      <b>
                        {getBenefitCountValue(benefitsCount)} (
                        {currencyFormat({ value: getTotalValue(benefits) })})
                      </b>
                    </TableCell>
                  ))}
                <TableCell>
                  <b>
                    {getBenefitCountTotalValue} (
                    {currencyFormat({ value: totalValue })})
                  </b>
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    </Grid>
  );
};

export default TableCarDemand;
export { TableCarDemand };
